import { StudentAvatar } from '@/components/shared/StudentAvatar/StudentAvatar';
import { useAuthStore } from '@/stores/authStore';
import type { Student } from '@/types/student';
import { cn } from '@/utils/cn';
import { Check, LockKeyhole } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface StudentSelectItemProps {
  student: Student;
  isSelected: boolean;
  isDisabled?: boolean;
  onToggle: () => void;
  isMultipleSchedulingEnabled: boolean;
}

export const StudentSelectItem = ({ student, isSelected, isDisabled = false, onToggle, isMultipleSchedulingEnabled }: StudentSelectItemProps) => {
  const { t } = useTranslation('scheduling', { keyPrefix: 'studentSelectItem' });

  const { studentsBgColors } = useAuthStore();

  const inputType = isMultipleSchedulingEnabled ? 'checkbox' : 'radio';
  const inputId = `student-${student.id}`;

  return (
    <label
      htmlFor={inputId}
      className={cn('relative cursor-pointer overflow-hidden rounded-lg border outline-none focus-within:ring-2 focus-within:ring-offset-2', {
        'border-fill-button-primary-default': isSelected && !isDisabled,
        'border-outline-input-default hover:border-outline-input-hover': !isSelected && !isDisabled,
        'border-outline-neutral cursor-not-allowed opacity-60': isDisabled,
      })}
    >
      {/* Hidden native input */}
      <input
        type={inputType}
        id={inputId}
        name='selectedStudents'
        disabled={isDisabled}
        checked={isSelected}
        onChange={onToggle}
        className='sr-only'
        aria-describedby={isDisabled ? `${inputId}-reason` : undefined}
      />

      <div className='flex h-full flex-col gap-3'>
        <div className='w-full'>
          {isDisabled ? (
            <div className='bg-fill-background-light flex items-center justify-center p-2 pb-0'>
              <div className='flex size-[90px] flex-col items-center justify-center gap-1'>
                <LockKeyhole className='size-6' aria-hidden='true' />
                <p className='text-body-s-regular text-center'>{t('noSessions')}</p>
              </div>
            </div>
          ) : (
            <div className='relative flex items-center justify-center p-2 pb-0'>
              {isSelected ? (
                <div className='bg-fill-button-primary-default absolute top-0 right-0 flex size-5 items-center justify-center rounded-md'>
                  <Check className='size-5 text-white' aria-hidden='true' />
                </div>
              ) : (
                <div className='border-outline-checkbox rounded-checkbox absolute top-0 right-0 size-5 border bg-white' aria-hidden='true' />
              )}
              <StudentAvatar
                student={{
                  ...student,
                  bgColor: studentsBgColors.get(student.id) ?? '#eee',
                }}
                className='size-[90px]'
                radius={3}
              />
            </div>
          )}
        </div>

        <div
          className={cn('text-body-m-semibold flex flex-col px-2 pb-3 text-center', {
            'text-body-m-regular text-text-default-secondary': isDisabled,
          })}
        >
          <p className='max-w-[90px] overflow-hidden text-ellipsis'>{student.firstName}</p>
          <p className='max-w-[90px] overflow-hidden text-ellipsis'>{student.lastName}</p>
        </div>
      </div>

      {isDisabled && (
        <span id={`${inputId}-reason`} className='sr-only'>
          {t('accessibility.disabledReason.noSessions', { studentName: `${student.firstName} ${student.lastName}` })}
        </span>
      )}
    </label>
  );
};
